// Hook que carga las sesiones de UN paciente específico, en estados
// terminales ('completada' / 'cancelada' / 'interrumpida'), ordenadas
// por started_at desc. Usado por el tab Sesiones del DetallePaciente
// (Sprint 3 B4b).
//
// Diferencia con useSesionesKine:
// - useSesionesKine: TODAS las sesiones del kine autenticado (todos sus
//   pacientes). Cross-paciente, cross-ejercicio.
// - useSesionesPaciente: solo las sesiones del pacienteId que se le pasa.
//   El kine filtrado por RLS (sesiones_select_kine USING
//   kine_id = auth.uid()) — si el paciente no es del kine autenticado, la
//   query devuelve [] naturalmente sin exponer data.
//
// Sprint 3 Approach A nota: el hito B4b originalmente decía "filtradas
// por tratamiento activo". Post-Approach A esto ya no aplica — todos los
// pacientes tienen 1 contenedor invisible, el filtro no discrimina.
// Filtro efectivo = paciente_id.

import { useEffect, useState } from "react";
import { supabase } from "../client";
import type {
  SesionEstadoListado,
  SesionKineRow,
} from "./useSesionesKine";

interface State {
  data: SesionKineRow[] | null;
  error: string | null;
  loading: boolean;
}

interface SesionRowRaw {
  id: string;
  started_at: string;
  ended_at: string | null;
  ejercicio_id: string;
  paciente_id: string;
  total_reps: number;
  score_promedio: number | null;
  duration_sec: number | null;
  estado: SesionEstadoListado;
  ejercicios: { nombre: string } | null;
  pacientes: { nombre: string; apellido: string } | null;
}

export function useSesionesPaciente(
  pacienteId: string | undefined,
): State {
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: !!pacienteId,
  });

  useEffect(() => {
    if (!pacienteId) {
      setState({ data: null, error: null, loading: false });
      return;
    }

    let cancelled = false;
    setState({ data: null, error: null, loading: true });

    (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select(
          `
          id,
          started_at,
          ended_at,
          ejercicio_id,
          paciente_id,
          total_reps,
          score_promedio,
          duration_sec,
          estado,
          ejercicios!inner(nombre),
          pacientes!inner(nombre, apellido)
        `,
        )
        .eq("paciente_id", pacienteId)
        .neq("estado", "en_progreso")
        .order("started_at", { ascending: false })
        .returns<SesionRowRaw[]>();

      if (cancelled) return;

      if (error) {
        console.warn("[useSesionesPaciente] error cargando sesiones", error);
        setState({ data: null, error: error.message, loading: false });
        return;
      }

      const rows: SesionKineRow[] = (data ?? []).map((r) => ({
        id: r.id,
        started_at: r.started_at,
        ended_at: r.ended_at,
        ejercicio_id: r.ejercicio_id,
        ejercicio_nombre: r.ejercicios?.nombre ?? r.ejercicio_id,
        paciente_id: r.paciente_id,
        paciente_nombre: r.pacientes
          ? `${r.pacientes.nombre} ${r.pacientes.apellido}`.trim()
          : "Paciente desconocido",
        total_reps: r.total_reps,
        score_promedio: r.score_promedio,
        duration_sec: r.duration_sec,
        estado: r.estado,
      }));

      setState({ data: rows, error: null, loading: false });
    })();

    return () => {
      cancelled = true;
    };
  }, [pacienteId]);

  return state;
}
